import { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { HomiLogo } from '@/components/HomiLogo';
import {
  LayoutDashboard, Sparkles, ShieldCheck, Briefcase, Users,
  FileText, ScrollText, Map, LogOut, Crown, Menu, X, Phone, Tag,
} from 'lucide-react';

const NAV = [
  { to: '/dashboard', label: 'Today', icon: LayoutDashboard, exact: true, testId: 'nav-today' },
  { to: '/ai', label: 'AI Employee', icon: Sparkles, testId: 'nav-ai' },
  { to: '/call', label: 'Call Command', icon: Phone, testId: 'nav-call', accent: 'plum' },
  { to: '/leads', label: 'Lead Shield', icon: ShieldCheck, testId: 'nav-leads' },
  { to: '/deals', label: 'Deal Desk', icon: Briefcase, testId: 'nav-deals' },
  { to: '/clients', label: 'Clients', icon: Users, testId: 'nav-clients' },
  { to: '/listings', label: 'Listing Legend', icon: FileText, testId: 'nav-listings' },
  { to: '/contracts', label: 'Contract Clerk', icon: ScrollText, testId: 'nav-contracts' },
  { to: '/intel', label: 'Neighborhood Intel', icon: Map, testId: 'nav-intel' },
  { to: '/pricing', label: 'Pricing', icon: Tag, testId: 'nav-pricing' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close drawer when route changes
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const doLogout = async () => {
    await logout();
    nav('/login');
  };

  return (
    <div className="min-h-screen w-full lg:flex" style={{ background: 'var(--ivory)' }}>
      {/* Mobile top bar */}
      <div
        className="lg:hidden flex items-center justify-between px-5 py-4 border-b sticky top-0 z-30"
        style={{ borderColor: 'var(--line)', background: '#FBF6EC' }}
      >
        <HomiLogo size={32} />
        <button
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          data-testid="mobile-menu-btn"
          className="p-2"
          style={{ color: 'var(--sage)' }}
        >
          <Menu size={22} />
        </button>
      </div>

      {/* Backdrop */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-40"
          style={{ background: 'rgba(44,44,44,0.45)' }}
          onClick={() => setOpen(false)}
          data-testid="mobile-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`
          fixed inset-y-0 left-0 z-50 w-64 shrink-0 border-r flex flex-col
          transform transition-transform duration-300 ease-out
          ${open ? 'translate-x-0' : '-translate-x-full'}
          lg:static lg:translate-x-0 lg:z-auto
        `}
        style={{ borderColor: 'var(--line)', background: '#FBF6EC' }}
      >
        <div className="px-6 pt-7 pb-6 flex items-center justify-between">
          <HomiLogo size={38} />
          <button
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            data-testid="close-sidebar-btn"
            className="lg:hidden p-1"
            style={{ color: 'var(--muted)' }}
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 px-2 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, exact, testId, accent }) => (
            <NavLink
              key={to}
              to={to}
              end={exact}
              data-testid={testId}
              className={({ isActive }) => `hq-nav-link ${isActive ? 'active' : ''}`}
            >
              <Icon size={16} strokeWidth={1.4} style={accent === 'plum' ? { color: 'var(--plum)' } : undefined} />
              <span className="uppercase">{label}</span>
              {accent === 'plum' && (
                <span
                  className="ml-auto text-[0.6rem] tracking-widest"
                  style={{ color: 'var(--plum)' }}
                >NEW</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 mx-3 mb-4 hq-card">
          <div className="flex items-center gap-2 mb-2">
            <Crown size={14} style={{ color: 'var(--gold)' }} />
            <span className="hq-eyebrow">Owner</span>
          </div>
          <div className="text-sm font-medium" style={{ color: 'var(--ink)' }} data-testid="user-name">
            {user?.name}
          </div>
          <div className="text-xs mb-3" style={{ color: 'var(--muted)' }} data-testid="user-email">
            {user?.email}
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className="flex items-center gap-2 text-xs uppercase tracking-widest"
            style={{ color: 'var(--muted)' }}
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 w-full relative">
        <div className="max-w-[1240px] mx-auto px-5 md:px-8 lg:px-10 py-6 md:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
